import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Banner from "./Bannner";
import ContactFooter from "./ContactFooter";
import HtmlRenderer from "./HtmlRenderer";
import { getNewsCategoryLabel } from "../constants/newsCategories";
import glv from "../utils/getLocalizedValue";
import supabase from "../utils/supabaseClient";

function getPreferredLang() {
  return localStorage.getItem("preferredLang") || "en";
}

function formatDate(dateString) {
  if (!dateString) return "";
  return String(dateString).slice(0, 10);
}

function NewsDetailPage() {
  const { newsId } = useParams();
  const [newsItem, setNewsItem] = useState(null);

  useEffect(() => {
    async function getNewsDetail() {
      if (!supabase) return;

      const { data, error } = await supabase
        .from("news_advanced")
        .select("*")
        .eq("id", newsId)
        .single();

      if (!error && data) {
        setNewsItem(data);
      }
    }

    getNewsDetail();
  }, [newsId]);

  const localized = useMemo(() => {
    if (!newsItem) return null;

    const preferredLang = getPreferredLang();
    const useEnglish = newsItem.is_english_only || preferredLang !== "ko";

    return {
      ...newsItem,
      localizedTitle: useEnglish ? newsItem.title : newsItem.title_korean,
      localizedHtml: useEnglish ? newsItem.html : newsItem.html_korean,
    };
  }, [newsItem]);

  return (
    <div
      className="w-full flex-grow overflow-y-scroll overflow-x-hidden"
      style={{ scrollSnapType: "y proximity" }}
    >
      <Banner
        title={glv("News and Updates", "최신 뉴스")}
        src={require("../assets/banner/news-banner.jpg")}
      />

      <main className="flex flex-col items-center pb-[10rem]">
        <div className="container max-w-[70rem] w-full font-pretendard px-5 sm:px-0">
          {!localized && (
            <div className="w-full bg-white/90 p-8 text-center text-slate-500">
              {glv("Loading...", "불러오는 중...")}
            </div>
          )}

          {localized && (
            <article className="w-full bg-white/90 p-6 sm:p-10">
              <header className="w-full bg-slate-100 p-6 sm:p-8 mt-6 sm:mt-8 mb-10">
                <nav
                  aria-label="Breadcrumb"
                  className="w-full text-sm text-slate-500 mb-6"
                >
                  <Link
                    to="/company/news?category=all"
                    className="hover:text-slate-900"
                  >
                    {glv("Newsroom", "뉴스룸")}
                  </Link>
                  <span className="mx-2 text-slate-400">&gt;</span>
                  <Link
                    to={`/company/news?category=${localized.type}`}
                    className="hover:text-slate-900"
                  >
                    {getNewsCategoryLabel(localized.type)}
                  </Link>
                </nav>

                <div className="w-full">
                  <h1 className="text-2xl sm:text-4xl font-[800] leading-[1.2] text-slate-900">
                    {localized.localizedTitle}
                  </h1>
                  <span className="block text-xs sm:text-sm text-slate-500 mt-3">
                    {formatDate(localized.date)}
                  </span>
                </div>
              </header>

              {localized.cover_image_url && (
                <div className="w-full max-w-[680px] mx-auto mt-6 mb-12">
                  <img
                    src={localized.cover_image_url}
                    alt={localized.localizedTitle}
                    className="w-full h-auto object-cover"
                  />
                </div>
              )}

              <div className="text-sm text-slate-500 mb-5">
                {getNewsCategoryLabel(localized.type)}
              </div>

              <div className="prose prose-slate max-w-none font-pretendard tracking-[-0.015em]">
                <HtmlRenderer htmlString={localized.localizedHtml} />
              </div>
            </article>
          )}
        </div>
      </main>

      <ContactFooter />
    </div>
  );
}

export default NewsDetailPage;
